import { computed, type Ref, type ComputedRef } from 'vue';
import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
import type { CognitoCurrentView } from './entry-details-lightbox.types';
import { getEntryNumberFromId } from './use-entry-details-layout';

type NavigationFormMeta = Pick<UserFormMeta, 'UrlName' | 'EntryViews'>;

export interface UseEntryDetailsNavigationOptions {
	organizationCode: Ref<string> | ComputedRef<string>;
	formMeta: Ref<NavigationFormMeta | null> | ComputedRef<NavigationFormMeta | null>;
	currentView: Ref<CognitoCurrentView | null> | ComputedRef<CognitoCurrentView | null>;
	viewId: Ref<string> | ComputedRef<string>;
	internalEntryId: Ref<string> | ComputedRef<string>;
	entryId: Ref<string> | ComputedRef<string>;
}

export function useEntryDetailsNavigation({
	organizationCode,
	formMeta,
	currentView,
	viewId,
	internalEntryId,
	entryId
}: UseEntryDetailsNavigationOptions) {
	const resolvedViewId = computed(() => {
		if (viewId.value)
			return viewId.value;

		return currentView.value?.get_Id?.() || null;
	});

	const viewMeta = computed(() => {
		const vid = resolvedViewId.value;
		if (!vid)
			return null;

		return formMeta.value?.EntryViews?.find(view => view.Id === vid) || null;
	});

	const getViewParam = () => {
		if (viewMeta.value?.Id) {
			const viewNumber = viewMeta.value.Id.split('-')[1];
			const internalName = viewMeta.value.InternalName || '';
			if (viewNumber)
				return `${viewNumber}-${internalName}`;
		}

		const currentViewId = currentView.value?.get_Id?.() || '';
		if (!currentViewId)
			return null;

		if (!formMeta.value?.EntryViews?.some(view => view.Id === currentViewId))
			return null;

		const currentViewNumber = currentViewId.split('-')[1];
		if (!currentViewNumber)
			return null;

		const currentViewInternalName = currentView.value?.get_InternalName?.() || '';
		return `${currentViewNumber}-${currentViewInternalName}`;
	};

	const entryDetailsRoute = computed(() => {
		const id = internalEntryId.value || entryId.value;
		const entryNumber = getEntryNumberFromId(id);
		if (!entryNumber)
			return null;

		const orgCode = organizationCode.value.trim();
		const formUrlName = (formMeta.value?.UrlName || '').trim();

		if (!orgCode || !formUrlName)
			return null;

		const encodedOrgCode = encodeURIComponent(orgCode);
		const encodedFormUrlName = encodeURIComponent(formUrlName);
		const viewParam = getViewParam();

		if (viewParam)
			return `/${encodedOrgCode}/${encodedFormUrlName}/${encodeURIComponent(viewParam)}/${encodeURIComponent(entryNumber)}`;

		return `/${encodedOrgCode}/${encodedFormUrlName}/entries/${encodeURIComponent(entryNumber)}`;
	});

	return {
		entryDetailsRoute
	};
}
